import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { CreditCard, Calendar, CheckCircle, Clock, AlertCircle, ArrowRight, RefreshCw } from 'lucide-react';
import { subscriptionAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';

// Plan durations in days for detecting custom extensions
const PLAN_DURATIONS_BY_ID = {
  'PLAN_TRIAL': 15,
  'PLAN_FREE_TRIAL': 15,
  'PLAN_1_MONTH': 30,
  'PLAN_3_MONTHS': 90,
  'PLAN_6_MONTHS': 180,
  'PLAN_12_MONTHS': 365,
  'PLAN_LIFETIME': 1825,
  'PLAN_5_YEARS': 1825
};

// Plan durations based on display name (for custom plan IDs)
const PLAN_DURATIONS_BY_NAME = {
  'Trial': 15,
  'Trial Plan': 15,
  '1 Month': 30,
  '3 Months': 90,
  '6 Months': 180,
  '12 Months': 365,
  '1 Year': 365,
  '5 Years': 1825,
  'Lifetime': 1825
};

const Subscription = () => {
  const navigate = useNavigate();
  const { currentUser, refreshUser } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);

  useEffect(() => {
    // Refresh user data to get latest subscription info
    refreshUser();
    loadSubscriptionInfo();
  }, []);

  const loadSubscriptionInfo = async () => {
    try {
      setLoading(true);
      const data = await subscriptionAPI.getStatus();
      setSubscriptionInfo(data);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load subscription information',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get display plan name (handles custom extensions)
  const getDisplayPlanName = () => {
    if (!subscriptionInfo) return 'No Plan';
    
    const daysRemaining = subscriptionInfo.daysRemaining || 0;
    const planName = subscriptionInfo?.planDetails?.name || subscriptionInfo?.planName || subscriptionInfo?.plan;
    
    // Get expected duration - first try by plan ID, then by display name
    let expectedDuration = PLAN_DURATIONS_BY_ID[subscriptionInfo.plan] || 0;
    if (expectedDuration === 0 && planName) {
      expectedDuration = PLAN_DURATIONS_BY_NAME[planName] || 0;
    }
    
    // If days remaining exceeds expected plan duration by more than 10%, or backend flagged inconsistency
    const isCustomExtended = subscriptionInfo.dataInconsistent || 
                             (expectedDuration > 0 && daysRemaining > (expectedDuration * 1.1));
    
    if (isCustomExtended) {
      return 'Custom Plan';
    }
    
    return planName || 'No Plan';
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-500">Active</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-500">Pending Approval</Badge>;
      case 'expired':
        return <Badge className="bg-red-500">Expired</Badge>;
      case 'rejected':
        return <Badge className="bg-red-600">Rejected</Badge>;
      default:
        return <Badge className="bg-gray-500">Unknown</Badge>;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="w-12 h-12 text-green-500" />;
      case 'pending':
        return <Clock className="w-12 h-12 text-yellow-500" />;
      case 'expired':
      case 'rejected':
        return <AlertCircle className="w-12 h-12 text-red-500" />;
      default:
        return <CreditCard className="w-12 h-12 text-gray-500" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  const isAdmin = currentUser?.role === 'admin';
  const isStaff = ['staff', 'manager', 'stakeholder', 'receptionist'].includes(currentUser?.role);

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-foreground">Subscription</h1>
        <Button
          variant="outline"
          size="sm"
          onClick={loadSubscriptionInfo}
          disabled={loading}
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Current Plan Card */}
      <Card className="border-0 shadow-lg">
        <CardHeader className="bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-t-lg">
          <CardTitle className="flex items-center gap-2 dark:text-foreground">
            <CreditCard className="w-5 h-5" />
            Current Plan
          </CardTitle>
          <CardDescription className="text-teal-100">
            Your subscription details
          </CardDescription>
        </CardHeader>
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row items-center gap-6">
            <div className="flex-shrink-0">
              {getStatusIcon(subscriptionInfo?.status)}
            </div>
            
            <div className="flex-1 text-center sm:text-left">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-foreground">
                {getDisplayPlanName()}
              </h2>
              <div className="mt-2">
                {getStatusBadge(subscriptionInfo?.status)}
              </div>
              
              {subscriptionInfo?.status === 'active' && (
                <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500 dark:text-muted-foreground">Started</p>
                    <p className="font-medium dark:text-foreground">
                      {subscriptionInfo?.startDate 
                        ? new Date(subscriptionInfo.startDate).toLocaleDateString('en-IN')
                        : '-'}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500 dark:text-muted-foreground">Expires</p>
                    <p className="font-medium dark:text-foreground">
                      {subscriptionInfo?.expiryDate 
                        ? new Date(subscriptionInfo.expiryDate).toLocaleDateString('en-IN')
                        : '-'}
                    </p>
                  </div>
                  {subscriptionInfo?.daysRemaining !== undefined && (
                    <div className="col-span-2">
                      <p className="text-gray-500 dark:text-muted-foreground">Days Remaining</p>
                      <p className={`font-bold text-lg ${
                        subscriptionInfo.daysRemaining < 7 ? 'text-red-600' : 
                        subscriptionInfo.daysRemaining < 30 ? 'text-yellow-600' : 'text-green-600'
                      }`}>
                        {subscriptionInfo.daysRemaining} days
                      </p>
                    </div>
                  )}
                </div>
              )}

              {subscriptionInfo?.status === 'pending' && (
                <p className="mt-4 text-yellow-700 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/30 p-3 rounded-lg text-sm">
                  Your subscription request is pending approval. You will be notified once approved.
                </p>
              )}

              {subscriptionInfo?.status === 'expired' && (
                <p className="mt-4 text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/30 p-3 rounded-lg text-sm">
                  Your subscription has expired. Please renew to continue using all features.
                </p>
              )}
            </div>
          </div>

          {/* Actions for Admin only */}
          {isAdmin && (
            <div className="mt-6 pt-6 border-t flex flex-col sm:flex-row gap-3">
              {subscriptionInfo?.status === 'active' && subscriptionInfo?.daysRemaining < 30 && (
                <Button 
                  onClick={() => navigate('/choose-plan')}
                  className="bg-teal-600 hover:bg-teal-700"
                >
                  Renew Subscription
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              )}
              
              {(subscriptionInfo?.status === 'expired' || !subscriptionInfo?.hasSubscription) && (
                <Button 
                  onClick={() => navigate('/choose-plan')}
                  className="bg-teal-600 hover:bg-teal-700"
                >
                  Choose a Plan
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              )}

              <Button 
                variant="outline"
                onClick={() => navigate('/choose-plan')}
              >
                View All Plans
              </Button>
            </div>
          )}

          {/* Info for Staff */}
          {isStaff && (
            <div className="mt-6 pt-6 border-t dark:border-border">
              <p className="text-sm text-gray-500 dark:text-muted-foreground flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Subscription is managed by your organization admin
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Features Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg dark:text-foreground">Plan Features</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-3">
            {[
              'Unlimited Bookings',
              'Lead Management',
              'Inventory Tracking',
              'Financial Reports',
              'Staff Management',
              'Calendar View',
              'Mobile App Access',
              'Email Notifications'
            ].map((feature, index) => (
              <li key={index} className="flex items-center gap-2 text-sm dark:text-foreground">
                <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                {feature}
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      {/* Contact Support */}
      <Card className="bg-gray-50 dark:bg-secondary">
        <CardContent className="p-4">
          <p className="text-sm text-gray-600 dark:text-muted-foreground text-center">
            Need help with your subscription? Contact us at{' '}
            <a href="mailto:info@myvenuemate.in" className="text-teal-600 dark:text-teal-400 font-medium">
              info@myvenuemate.in
            </a>
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default Subscription;
